import {
  MonthlyReturn,
  MonthlyReturnStatus,
  StateCode,
  buildEmptyStateData,
  buildEmptyDaTesting,
} from '../../models/monthly-return';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Demo lists — small enough to be easy to scan in the UI.
const DEMO_RAIL_INFRASTRUCTURE_MANAGERS: string[] = [
  'Operator Rail Pty Ltd',
  'ARTC',
  'V/Line Corporation',
];

const DEMO_ROLLING_STOCK_OPERATORS: string[] = [
  '3801 LTD',
  'Aurizon Bulk Central Pty Ltd',
  'Aurizon Operations Limited',
  'Brimble Rail Pty Ltd',
  'Great Southern Rail Pty Ltd',
  'Holdco Holdings Pty Ltd',
  'John Holland Pty Ltd',
  'Lachlan Valley Railway Society Co-operative Ltd',
  "Laing O'Rourke Australia Construction Pty Ltd",
  'Martinus Rail Pty Ltd',
  'NSW Trains',
  'One Rail Australia (FLA) Pty Ltd',
  'Pacific National Pty Ltd',
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function buildDueDate(year: number, monthIndex: number): string {
  const dueMonth = monthIndex + 1 > 12 ? 1 : monthIndex + 1;
  const dueYear = monthIndex + 1 > 12 ? year + 1 : year;
  return `15/${pad2(dueMonth)}/${dueYear}`;
}

function buildSubmittedDate(year: number, monthIndex: number, offset: number): string {
  const day = ((offset * 3) % 13) + 2;
  const subMonth = monthIndex + 1 > 12 ? 1 : monthIndex + 1;
  const subYear = monthIndex + 1 > 12 ? year + 1 : year;
  return `${pad2(day)}/${pad2(subMonth)}/${subYear}`;
}

function previousPeriodLabel(year: number, monthIndex: number): string {
  let prevMonth = monthIndex - 1;
  let prevYear = year;
  if (prevMonth < 1) { prevMonth = 12; prevYear = year - 1; }
  return `${MONTH_ABBR[prevMonth - 1]} ${prevYear}`;
}

function generateRandom(min: number, max: number, seed: number, salt: number): number {
  const x = Math.sin(seed * 9999 + salt * 13) * 10000;
  const frac = x - Math.floor(x);
  return Math.floor(min + frac * (max - min));
}

function buildMonthlyReturn(
  id: number,
  monthIndex: number,
  year: number,
  status: MonthlyReturnStatus,
  states: StateCode[],
  populated: boolean,
): MonthlyReturn {
  const monthName = MONTH_NAMES[monthIndex - 1];

  // Submitted/in-progress months show their selections; current period starts blank.
  const interfaces = {
    railInfrastructureManagers: populated ? [...DEMO_RAIL_INFRASTRUCTURE_MANAGERS] : [],
    rollingStockOperators: populated ? [...DEMO_ROLLING_STOCK_OPERATORS] : [],
    previousRailInfrastructureManagers: [...DEMO_RAIL_INFRASTRUCTURE_MANAGERS],
    previousRollingStockOperators: [...DEMO_ROLLING_STOCK_OPERATORS],
    previousPeriodLabel: previousPeriodLabel(year, monthIndex),
  };

  const perState = states.map((state) => {
    const data = buildEmptyStateData(state);
    if (populated) {
      data.general.totalNumber = generateRandom(180, 220, id, monthIndex);
      data.general.totalHours = generateRandom(28000, 32000, id, monthIndex + 1);
      data.movement.passengerTrainKilometres = generateRandom(35000, 80000, id, monthIndex + 2);
      data.movement.freightTrainKilometres = generateRandom(85000, 100000, id, monthIndex + 3);
      data.movement.passengerTrainKm = generateRandom(0, 20, id, monthIndex + 4);
      data.movement.freightTrainKm = generateRandom(0, 50, id, monthIndex + 5);
      data.movement.maintenanceTrainKm = generateRandom(8000, 12000, id, monthIndex + 6);
      data.movement.passengerJourneys = generateRandom(80, 200, id, monthIndex + 7);

      data.daTesting = buildEmptyDaTesting();
      const trainDriver = data.daTesting[0];
      trainDriver.rows[0].counts.preSignOn.Negative.employee = generateRandom(40, 200, id, monthIndex + 10);
      trainDriver.rows[0].counts.preSignOn.Negative.contractor = generateRandom(0, 12, id, monthIndex + 11);
      trainDriver.rows[3].counts.preSignOn.Negative.employee = generateRandom(20, 80, id, monthIndex + 12);
    }
    return data;
  });

  return {
    id,
    organisationName: 'Operator Rail Pty Ltd',
    reportingMonth: `${monthName} ${year}`,
    monthIndex,
    year,
    dueDate: buildDueDate(year, monthIndex),
    submittedDate: status === 'Submitted' ? buildSubmittedDate(year, monthIndex, id) : null,
    status,
    states,
    interfaces,
    perState,
  };
}

const ALL_STATES: StateCode[] = ['NSW', 'VIC', 'QLD', 'WA'];

function statusFor(monthIndex: number, year: number): MonthlyReturnStatus {
  if (year === 2026 && monthIndex <= 4) return 'Submitted';
  if (year === 2026 && monthIndex === 5) return 'In Progress';
  if (year === 2025 && monthIndex === 12) return 'Overdue';
  return 'Submitted';
}

function buildAll(): MonthlyReturn[] {
  const items: MonthlyReturn[] = [];
  let id = 1;
  for (let year = 2024; year <= 2026; year++) {
    const startMonth = year === 2024 ? 7 : 1;
    const endMonth = year === 2026 ? 5 : 12;
    for (let m = startMonth; m <= endMonth; m++) {
      const status = statusFor(m, year);
      const populated = status === 'Submitted' || status === 'Overdue';
      items.push(buildMonthlyReturn(id, m, year, status, ALL_STATES, populated));
      id++;
    }
  }
  return items
    .sort((a, b) => (a.year !== b.year ? b.year - a.year : b.monthIndex - a.monthIndex))
    .map((mr, idx) => ({ ...mr, id: idx + 1 }));
}

export class GetMonthlyReturns {
  static monthlyReturns: MonthlyReturn[] = buildAll();
}
